<template>
  <div>
    <BlBackdrop
      title="Edit Package"
      label="Update"
      :submitLoading="submitLoading"
      :disabled="isDisabledSubmit"
      @backdrop-close="$emit('close-modal')"
      @backdrop-action="onSubmit"
    >
      <div
        class="flex flex-col gap-4 overflow-y-auto"
        :style="{ width: '40rem', maxHeight: 'calc(100vh - 20rem)' }"
      >
        <div class="px-8 flex flex-col gap-4">
          <BlInputText :input="nameInput" @changed="inputHandler" />
          <BlInputText :input="descriptionInput" @changed="inputHandler" />

          <div class="flex flex-col gap-2">
            <BlTextSubtitle1 text="Add package items" />
            <BlTextBody2
              text="You can add multiple items to a package."
              class="text-slate-700"
            />
          </div>

          <div
            class="py-2 px-4 grid grid-cols-3 gap-4 bg-slate-100 rounded border border-slate-300"
          >
            <div class="flex items-center flex-col gap-1">
              <BlTextSubtitle1 :text="totalItems" />
              <BlTextBody3 text="Total items" class="text-slate-700" />
            </div>
            <div class="flex items-center flex-col gap-1">
              <BlTextSubtitle1 :text="totalCredits" />
              <BlTextBody3 text="Total credits" class="text-slate-700" />
            </div>
            <div class="flex items-center flex-col gap-1">
              <div>
                <BlTextSubtitle1 :text="currentCenter.currency_sym" />
                <BlTextSubtitle1 :text="priceFormats(totalPrice)" />
              </div>
              <BlTextBody3 text="Total price" class="text-slate-700" />
            </div>
          </div>

          <div
            v-for="(item, idx) in packageServicesInput"
            :key="item.id"
            class="py-4 px-4 flex flex-col gap-4 bg-slate-100 rounded border border-slate-300"
          >
            <div class="flex items-end gap-2">
              <div class="flex-grow">
                <BlSelectBox
                  label="Item"
                  asterisk
                  v-model="packageServicesInput[idx].package_item_id"
                  placeholder="Select item"
                  :options="creditPackageItemOptions"
                  @change="onChangeService(idx, $event)"
                />
              </div>
              <BlInputButton
                type="plain"
                icon="delete"
                :disabled="totalItems < 2"
                @clicked="onRemoveService(item.id)"
              />
            </div>
            <div
              class="py-3 px-4 flex items-center flex-col gap-1 bg-slate-300 rounded"
            >
              <div class="flex items-center gap-3">
                <BlInputButton
                  type="outlined"
                  icon="minus"
                  dense
                  class="rounded-full"
                  :disabled="packageServicesInput[idx].session_credit <= 0"
                  @clicked="
                    onInputCredits(
                      idx,
                      packageServicesInput[idx].session_credit - 1,
                      item.standard_retail_price
                    )
                  "
                />
                <input
                  type="number"
                  :name="`credit_count_${item.id}`"
                  :id="`credit_count_${item.id}`"
                  :min="1"
                  :value="packageServicesInput[idx].session_credit"
                  @input="
                    onInputCredits(
                      idx,
                      $event.target.value,
                      item.standard_retail_price
                    )
                  "
                  class="outline-none w-104 h-20 flex items-center justify-center rounded border border-slate-500 text-center text-6xl font-bold text-dark-purple-100 focus:border-light-purple"
                  :class="[
                    packageServicesInput[idx].session_credit <= 0
                      ? 'text-error-300'
                      : 'text-dark-purple-100'
                  ]"
                />
                <BlInputButton
                  type="outlined"
                  icon="plus"
                  dense
                  class="rounded-full"
                  @clicked="
                    onInputCredits(
                      idx,
                      packageServicesInput[idx].session_credit + 1,
                      item.standard_retail_price
                    )
                  "
                />
              </div>
              <BlTextBody3 text="Credits" class="text-slate-700" />
            </div>
            <div class="flex items-center gap-2">
              <BlInputText
                :input="{
                  label: 'Standard retail price',
                  model: priceFormats(item.standard_retail_price),
                  prefix: currentCenter.currency_sym,
                  prefixSpace: '3rem',
                  disabled: true
                }"
              />
              <div class="pt-6">
                <BlIcon
                  icon="horizontal-arrow"
                  color="slate-6"
                  class="h-6 w-6"
                />
              </div>
              <BlInputText
                :input="{
                  label: 'Total price',
                  inputType: 'number',
                  min: 0,
                  model: packageServicesInput[idx].total_price,
                  prefix: currentCenter.currency_sym,
                  prefixSpace: '3rem'
                }"
                @changed="onInputTotalPrice(idx, $event)"
              />
            </div>
          </div>

          <BlInputButton
            type="outlined"
            dense
            label="Add another item"
            @clicked="onAddAnotherService"
          />
        </div>

        <div class="w-full h-5 flex-shrink-0 bg-grey-100"></div>

        <div class="px-8">
          <div
            class="flex items-center mb-2 cursor-pointer"
            @click="setExpirationDateInput = !setExpirationDateInput"
          >
            <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
              <BlInputToggle
                :checked="setExpirationDateInput"
                @changed="setExpirationDateInput = !setExpirationDateInput"
              />
            </div>
            <BlTextSubtitle1 text="Set expiration date" />
          </div>
          <div class="mb-4">
            <BlTextBody2
              text="The expiration date determines the last day credits can be used to book an appointment—unused credits will be lost after this date, but booked appointments remain valid even if they occur later."
            />
          </div>
          <div v-if="setExpirationDateInput" class="grid grid-cols-2 gap-2">
            <BlInputText :input="expirationDaysInput" @changed="inputHandler" />
          </div>
        </div>

        <div class="w-full h-5 flex-shrink-0 bg-grey-100"></div>

        <div class="px-8">
          <div
            class="flex items-center mb-2 cursor-pointer"
            @click="setMaximumRescheduleInput = !setMaximumRescheduleInput"
          >
            <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
              <BlInputToggle
                :checked="setMaximumRescheduleInput"
                @changed="
                  setMaximumRescheduleInput = !setMaximumRescheduleInput
                "
              />
            </div>
            <BlTextSubtitle1 text="Set maximum reschedules" />
          </div>
          <div class="mb-4">
            <BlTextBody2
              text="If the reschedule limit is not specified, sessions using this credit can be rescheduled infinitely."
            />
          </div>
          <div v-if="setMaximumRescheduleInput" class="grid grid-cols-2 gap-2">
            <BlInputText
              :input="maximumRescheduleInput"
              @changed="inputHandler"
            />
          </div>
        </div>
      </div>

      <div class="absolute left-8 -bottom-14 z-10">
        <BlTooltip>
          <template v-if="creditPackage.in_use" v-slot:tooltip>
            <div class="w-48">
              This package cannot be deleted because it has been used by
              clients.
            </div>
          </template>
          <BlInputButton
            type="outlined-error"
            label="Delete package"
            :disabled="creditPackage.in_use"
            @clicked="deleteOpened = true"
          />
        </BlTooltip>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete package"
      :description="
        `You are about to delete <b>${creditPackage.name}</b>. Are you sure you want to proceed? This action is irreversible.`
      "
      :label="`Delete`"
      type="error"
      :submitLoading="submitLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onDelete"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayPrice } from "@/plugins/helper.js";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlTooltip from "../../BlTooltip.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlSelectBox from "../../BlSelectBox.vue";

export default {
  name: "EditPackageModal",
  components: {
    BlBackdrop,
    BlTextSubtitle1,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlTooltip,
    BlIcon,
    BlInputText,
    BlInputDate,
    BlInputButton,
    BlInputToggle,
    BlSelectBox
  },
  props: {
    creditPackage: { type: Object }
  },
  data() {
    return {
      submitLoading: false,
      deleteOpened: false,
      nameInput: {
        label: "Package name",
        asterisk: true,
        placeholder: "Type the name",
        model: this.creditPackage.name
      },
      descriptionInput: {
        label: "Description",
        placeholder: "Type the description",
        textarea: true,
        model: this.creditPackage.description
      },
      packageServicesInput: [...this.creditPackage.package_services],
      creditPackageItems: [],
      totalCreditPackageItems: 0,
      destroyServices: [],
      //
      setExpirationDateInput: this.creditPackage.expiration_date !== null,
      expirationDaysInput: {
        label: "Expiration date",
        model: this.creditPackage.expiration_date,
        min: 0,
        inputType: "number",
        suffix: "days after purchase"
      },
      setMaximumRescheduleInput: this.creditPackage.reschedule_credit !== null,
      maximumRescheduleInput: {
        label: "Maximum reschedule allowed",
        model: this.creditPackage.reschedule_credit,
        min: 1,
        inputType: "number",
        suffix: "time(s)"
      }
    };
  },
  computed: {
    ...mapGetters(["currentCenter"]),
    totalItems() {
      return this.packageServicesInput.length;
    },
    totalCredits() {
      let total = 0;
      this.packageServicesInput.forEach(i => {
        total += i.session_credit;
      });
      return total;
    },
    totalPrice() {
      let total = 0;
      this.packageServicesInput.forEach(i => {
        total += i.total_price;
      });
      return total;
    },
    creditPackageItemOptions() {
      const ids = this.packageServicesInput.map(i => i.package_item_id);
      const arr = this.creditPackageItems.map(i => {
        return { value: i.id, label: i.name, disabled: ids.includes(i.id) };
      });
      return arr;
    },
    isDisabledSubmit() {
      if (!this.nameInput.model) return true;
      if (!this.packageServicesInput.length) return true;

      let zeroValue = false;
      this.packageServicesInput.forEach(i => {
        if (!i.session_credit) zeroValue = true;
      });
      if (zeroValue) return true;

      return false;
    }
  },
  watch: {},
  async mounted() {
    const params = "?page=1&per_page=100&sort=name_asc&status=active";
    const { success, data } = await this.getCreditPackageItems({ params });
    if (!success) return;
    this.creditPackageItems = data.package_items;
    this.totalCreditPackageItems = data.total_count;
  },
  methods: {
    ...mapActions([
      "getCreditPackageItems",
      "updateCreditPackage",
      "deleteCreditPackage"
    ]),
    displayPrice,
    priceFormats(price = 0) {
      const settings = this.currentCenter.currency_settings;
      return this.displayPrice({ settings, price });
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    //
    onChangeService(idx, value) {
      const packageItem = this.creditPackageItems.find(i => i.id === value);
      this.packageServicesInput[idx].package_item_id = packageItem.id;
      this.packageServicesInput[idx].standard_retail_price =
        packageItem.standard_retail_price;
      this.packageServicesInput[idx].session_credit = 0;
      this.packageServicesInput[idx].total_price = 0;
    },
    onInputCredits(idx, value, srp) {
      const val = Number(value);
      this.packageServicesInput[idx].session_credit = val;
      this.packageServicesInput[idx].total_price = val * srp;
    },
    onInputTotalPrice(idx, payload) {
      const val = Number(payload.value);
      this.packageServicesInput[idx].total_price = val;
    },
    onAddAnotherService() {
      function getRandomId() {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let combination = "";
        for (let i = 0; i < 6; i++) {
          combination += chars[Math.round(Math.random() * 35)];
        }
        return `new-service-${combination}`;
      }
      const add = {
        id: getRandomId(),
        package_item_id: 0,
        standard_retail_price: 0,
        session_credit: 0,
        total_price: 0
      };
      this.packageServicesInput.push(add);
    },
    onRemoveService(id) {
      if (!id.toString().includes("new")) {
        this.destroyServices.push({ id, _destroy: 1 });
      }
      this.packageServicesInput = this.packageServicesInput.filter(
        i => i.id !== id
      );
    },
    //
    async onSubmit() {
      const services = this.packageServicesInput.map(i => {
        const useId = !i.id.toString().includes("new");
        return {
          ...(useId ? { id: i.id } : {}),
          package_item_id: i.package_item_id,
          session_credit: i.session_credit,
          total_price: i.total_price
        };
      });

      const params = {
        id: this.creditPackage.id,
        package: {
          name: this.nameInput.model,
          description: this.descriptionInput.model,
          expiration_date: null,
          reschedule_credit: null,
          package_services_attributes: [...services, ...this.destroyServices]
        }
      };

      if (this.setExpirationDateInput) {
        params.package.expiration_date = this.expirationDaysInput.model;
      }
      if (this.setMaximumRescheduleInput) {
        params.package.reschedule_credit = this.maximumRescheduleInput.model;
      }

      this.submitLoading = true;
      const { success } = await this.updateCreditPackage(params);
      this.submitLoading = false;
      if (!success) return;
      this.$emit("close-modal");
    },
    async onDelete() {
      const params = this.creditPackage;

      this.submitLoading = true;
      const { success } = await this.deleteCreditPackage(params);
      this.submitLoading = false;
      if (!success) return;
      this.$emit("close-modal");
      this.$router.push("/center/package");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/center/package/EditPackageModal.vue